import Task from '../../../types/Task';
import { Epic } from '../../../types/Epic';

export const NO_EPIC_KEY = 'no-epic';
export const NO_EPIC_TITLE = 'No Epic';

/** Row heights are fixed so the virtualizer can size the list without measuring. */
export const HEADER_ROW_HEIGHT = 24;
export const TASK_ROW_HEIGHT = 32;

export type DependencyGroup = { key: string; title: string; tasks: Task[] };

export type DependencyRow = { kind: 'header'; key: string; title: string } | { kind: 'task'; key: string; task: Task };

const selectedFirst = (tasks: Task[], selectedIds: ReadonlySet<number>) =>
  // Array#sort is stable, so unselected tasks keep their incoming order.
  tasks.sort((a, b) => Number(selectedIds.has(b.id)) - Number(selectedIds.has(a.id)));

/**
 * Group pickable tasks under their epic, ordering epics that contain a selected task
 * first and, within each group, selected tasks first. Callers recompute this only when
 * the dropdown opens so the order cannot shift while the user is clicking. (#2601)
 */
export function buildOrderedGroups(params: {
  options: Task[];
  epics: Epic[];
  selectedIds: ReadonlySet<number>;
  excludeIds: ReadonlySet<number>;
}): DependencyGroup[] {
  const { options, epics, selectedIds, excludeIds } = params;

  const tasksByEpicId = new Map<string, Task[]>();
  const tasksWithoutEpic: Task[] = [];

  for (const task of options) {
    if (excludeIds.has(task.id)) continue;
    if (!task.epicId) {
      tasksWithoutEpic.push(task);
      continue;
    }
    const key = String(task.epicId);
    const bucket = tasksByEpicId.get(key);
    if (bucket) bucket.push(task);
    else tasksByEpicId.set(key, [task]);
  }

  const groups: DependencyGroup[] = [];
  for (const epic of epics) {
    const tasks = tasksByEpicId.get(String(epic.id));
    if (!tasks?.length) continue;
    groups.push({ key: String(epic.id), title: epic.title, tasks: selectedFirst(tasks, selectedIds) });
  }

  const hasSelected = (group: DependencyGroup) => group.tasks.some((task) => selectedIds.has(task.id));
  groups.sort((a, b) => Number(hasSelected(b)) - Number(hasSelected(a)));

  if (tasksWithoutEpic.length) {
    groups.push({
      key: NO_EPIC_KEY,
      title: NO_EPIC_TITLE,
      tasks: selectedFirst(tasksWithoutEpic, selectedIds),
    });
  }

  return groups;
}

/** Flatten groups to a single row list the virtualizer can index, dropping groups the search empties. */
export function buildRows(groups: DependencyGroup[], searchTerm: string): DependencyRow[] {
  const term = searchTerm.trim().toLowerCase();
  const rows: DependencyRow[] = [];

  for (const group of groups) {
    const tasks = term ? group.tasks.filter((task) => task.title.toLowerCase().includes(term)) : group.tasks;
    if (!tasks.length) continue;

    rows.push({ kind: 'header', key: `header:${group.key}`, title: group.title });
    for (const task of tasks) rows.push({ kind: 'task', key: `task:${task.id}`, task });
  }

  return rows;
}

export const rowHeight = (row: DependencyRow) => (row.kind === 'header' ? HEADER_ROW_HEIGHT : TASK_ROW_HEIGHT);
